import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Drawer, Icon, Skeleton, Tag, Empty, Tooltip, Button, Modal, Input, message } from "antd";
import moment from "moment";

import { request } from "~/components/Instance";
import { toBRL } from "~/components/UI/InputCurrency";
import * as monthlyPaymentActions from "~/screens/Financial/MonthlyPayment/actions";

var Row = function(props) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', gap: 12 }}>
            <span style={{ color: '#8c8c8c', fontSize: 13, flexShrink: 0 }}>{props.label}</span>
            <span style={{ color: '#262626', fontSize: 13, fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>{props.value}</span>
        </div>
    );
};

var Section = function(props) {
    return (
        <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '4px 16px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, padding: '12px 0 8px' }}>
                <Icon type={props.icon} style={{ marginRight: 6 }} />{props.title}
            </div>
            {props.children}
        </div>
    );
};

var ReconciliationInfoDrawer = function(props) {
    var visible = props.visible;
    var recordId = props.recordId;
    var recordType = props.recordType || 'mensalidade';
    var onClose = props.onClose;
    var monthlyFilter = props.monthlyFilter;
    var dispatch = useDispatch();

    var [loading, setLoading] = useState(false);
    var [data, setData] = useState(null);
    var [siblings, setSiblings] = useState([]);
    var [undoModalOpen, setUndoModalOpen] = useState(false);
    var [observation, setObservation] = useState('');
    var [undoing, setUndoing] = useState(false);

    var handleConfirmUndo = function() {
        if (!data || !data.id) return;
        if (!observation.trim()) {
            message.warning('Informe uma observação');
            return;
        }
        setUndoing(true);
        request('DELETE', '/bank-reconciliation/reconcile/' + data.id, { observation: observation.trim() })
            .then(function() {
                message.success('Conciliação removida com sucesso');
                setUndoModalOpen(false);
                setObservation('');
                if (monthlyFilter) {
                    dispatch(monthlyPaymentActions.loadPayments(monthlyFilter));
                }
                onClose();
            })
            .catch(function(err) {
                var msg = (err && err.response && err.response.data && err.response.data.message) || 'Erro ao remover conciliação';
                message.error(msg);
            })
            .finally(function() {
                setUndoing(false);
            });
    };

    useEffect(function() {
        if (!visible || !recordId) return;
        setLoading(true);
        setData(null);
        setSiblings([]);
        request('GET', '/bank-reconciliation/by-reconcilable/' + recordType + '/' + recordId)
            .then(function(response) {
                var body = response.data || {};
                setData(body.data || null);
                setSiblings(body.siblings || []);
            })
            .catch(function() {
                setData(null);
                setSiblings([]);
            })
            .finally(function() {
                setLoading(false);
            });
    }, [visible, recordId, recordType]);

    var renderContent = function() {
        if (loading) {
            return (
                <div>
                    {/* Hero card skeleton */}
                    <div style={{ background: '#f5f5f5', borderRadius: 12, padding: '20px', marginBottom: 16, border: '1px solid #e8e8e8' }}>
                        <Skeleton active title={{ width: 100 }} paragraph={{ rows: 2, width: ['60%', '40%'] }} />
                    </div>
                    {/* Two section skeletons */}
                    {[0, 1].map(function(i) {
                        return (
                            <div key={i} style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
                                <Skeleton active title={{ width: 140 }} paragraph={{ rows: 3, width: ['90%', '70%', '50%'] }} />
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (!data) {
            return (
                <Empty description="Nenhuma conciliação encontrada" />
            );
        }

        var stmt = data.bank_statement || {};
        var account = stmt.bank_account || {};
        var reconciledBy = data.reconciled_by;
        var isCredit = stmt.transaction_type === 'credit';
        var amountColor = isCredit ? '#52c41a' : '#f5222d';

        return (
            <div>
                {/* Hero card */}
                <div style={{
                    background: isCredit ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)' : 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
                    borderRadius: 12, padding: '20px', marginBottom: 16,
                    border: '1px solid ' + (isCredit ? '#b7eb8f' : '#ffa39e'),
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <Icon type={isCredit ? 'arrow-down' : 'arrow-up'} style={{ color: amountColor, fontSize: 18 }} />
                        <Tag color={isCredit ? 'green' : 'red'} style={{ margin: 0 }}>
                            {isCredit ? 'Crédito' : 'Débito'}
                        </Tag>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: amountColor, marginBottom: 4 }}>
                        R$ {toBRL(stmt.amount || 0)}
                    </div>
                    <div style={{ fontSize: 13, color: '#595959' }}>
                        {moment(stmt.transaction_date).format('DD [de] MMMM [de] YYYY')}
                    </div>
                </div>

                {/* Bank Statement */}
                <Section icon="file-text" title="Lançamento bancário">
                    <Row label="Descrição" value={stmt.description || '-'} />
                    {stmt.document_number && <Row label="Documento" value={stmt.document_number} />}
                    {account.name && (
                        <Row label="Conta bancária" value={
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <Icon type="bank" style={{ color: '#1890ff' }} />{account.name}
                            </span>
                        } />
                    )}
                </Section>

                {/* Reconciliation */}
                <Section icon="link" title="Conciliação">
                    <Row label="Conciliado em" value={data.created_at ? moment(data.created_at).format('DD/MM/YYYY HH:mm') : '-'} />
                    {reconciledBy && (
                        <Row label="Conciliado por" value={
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <Icon type="user" style={{ color: '#1890ff' }} />{reconciledBy.name}
                            </span>
                        } />
                    )}
                    {data.justification && (
                        <Row label="Nota" value={
                            <Tooltip title={data.justification}>
                                <span style={{ fontStyle: 'italic', color: '#595959' }}>"{data.justification}"</span>
                            </Tooltip>
                        } />
                    )}
                </Section>

                {/* Siblings — other records linked to the same bank statement */}
                {siblings.length > 0 && (
                    <Section icon="team" title={'Estes também foram vinculados ao mesmo lançamento bancário (' + siblings.length + ')'}>
                        {siblings.map(function(sib, idx) {
                            var recType = sib.reconcilable_type || '';
                            var typeLabel = recType.indexOf('CustomerHonorary') > -1 ? 'Mensalidade'
                                : recType.indexOf('CustomerExtra') > -1 ? 'Extra'
                                : recType.indexOf('Refund') > -1 ? 'Comissão'
                                : recType.indexOf('ContaPagar') > -1 ? 'Despesa' : 'Outro';
                            var typeColor = recType.indexOf('CustomerHonorary') > -1 ? 'blue'
                                : recType.indexOf('CustomerExtra') > -1 ? 'purple'
                                : recType.indexOf('Refund') > -1 ? 'orange'
                                : recType.indexOf('ContaPagar') > -1 ? 'red' : 'default';
                            var r = sib.reconcilable || {};
                            var isExpense = recType.indexOf('ContaPagar') > -1 || recType.indexOf('BankReconciliationEntry') > -1;
                            var name = isExpense ? (r.description || '-') : (r.customer ? r.customer.name : '-');
                            var amount = recType.indexOf('CustomerHonorary') > -1
                                ? (parseFloat(r.amount) || 0) + (parseFloat(r.additionals_amount) || 0) + (parseFloat(r.amount13) || 0)
                                : (parseFloat(r.amount) || parseFloat(r.total) || 0);
                            var sibDate = r.due_at || r.due_date || r.apuration_at || r.created_at;
                            var dateLabel = recType.indexOf('CustomerHonorary') > -1 ? 'Venc' : 'Criado';
                            return (
                                <div key={sib.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: idx < siblings.length - 1 ? '1px solid #f0f0f0' : 'none', gap: 8 }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                            <Tag color={typeColor} style={{ margin: 0, fontSize: 10 }}>{typeLabel}</Tag>
                                            {sibDate && (
                                                <span style={{ fontSize: 11, color: '#8c8c8c', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                                    <Icon type="calendar" style={{ fontSize: 10 }} />
                                                    {dateLabel}: {moment(sibDate).format('DD/MM/YYYY')}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 13, color: '#262626', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                                    </div>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#389e0d', flexShrink: 0 }}>R$ {toBRL(amount)}</div>
                                </div>
                            );
                        })}
                    </Section>
                )}

                {/* Undo action */}
                <div style={{ marginTop: 24, padding: '16px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#cf1322', marginBottom: 6 }}>
                        <Icon type="warning" style={{ marginRight: 6 }} />Desfazer conciliação
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
                        O lançamento bancário voltará para pendente e o pagamento do registro será revertido.
                    </div>
                    <Button
                        type="danger"
                        icon="disconnect"
                        onClick={function() { setObservation(''); setUndoModalOpen(true); }}
                    >
                        Desfazer conciliação
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <>
            <Drawer
                title={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Icon type="bank" style={{ color: '#52c41a' }} />
                        <span style={{ fontWeight: 600 }}>Detalhes da conciliação</span>
                    </span>
                }
                placement="right"
                visible={visible}
                onClose={onClose}
                width={460}
                bodyStyle={{ background: '#fafafa', padding: 16 }}
            >
                {renderContent()}
            </Drawer>

            <Modal
                title={<span><Icon type="warning" style={{ color: '#f5222d', marginRight: 6 }} />Desfazer conciliação</span>}
                visible={undoModalOpen}
                okText="Confirmar"
                cancelText="Cancelar"
                okType="danger"
                confirmLoading={undoing}
                onOk={handleConfirmUndo}
                onCancel={function() { setUndoModalOpen(false); setObservation(''); }}
            >
                <p style={{ marginBottom: 12 }}>
                    Tem certeza que deseja desfazer esta conciliação? O lançamento bancário voltará para pendente e o registro será marcado como não pago.
                </p>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                    Observação <span style={{ color: '#f5222d' }}>*</span>
                </div>
                <Input.TextArea
                    rows={4}
                    value={observation}
                    onChange={function(e) { setObservation(e.target.value); }}
                    placeholder="Explique o motivo de desfazer esta conciliação..."
                    maxLength={500}
                    autoFocus
                />
                <div style={{ textAlign: 'right', fontSize: 11, color: '#bfbfbf', marginTop: 4 }}>
                    {observation.length}/500
                </div>
            </Modal>
        </>
    );
};

export default ReconciliationInfoDrawer;
